"use client"

import { memo } from "react"
import Link from "next/link"
import { SearchIcon } from "lucide-react"

import { RecentlySearches } from "@/features/layout/headers/recently-searches"

// Types
interface KeywordSuggestion {
  text: string
  name_type: "restaurant" | "dish"
}

// Keyword Suggestions Component
export const KeywordSuggestions = memo(
  ({
    suggestions,
    keyword,
    onSearch,
  }: {
    suggestions: KeywordSuggestion[]
    keyword: string
    onSearch: (text: string, type?: string) => void
  }) => {
    return (
      <div>
        <h3 className="text-sm font-medium mb-2">Gợi ý tìm kiếm</h3>
        <ul className="space-y-2">
          {suggestions?.map((suggestion) => (
            <li key={suggestion.text}>
              <Link
                href={`/search?keyword=${encodeURIComponent(
                  suggestion.text
                )}&type=${suggestion.name_type === "restaurant" ? "restaurant" : "dish"}`}
                className="flex items-center px-2 py-1.5 rounded-md hover:bg-gray-100 transition-colors"
                onClick={() => onSearch(suggestion.text, suggestion.name_type)}
              >
                <SearchIcon className="h-4 w-4 mr-3 text-gray-500" />
                <span>{suggestion.text}</span>
              </Link>
            </li>
          ))}

          <li>
            <Link
              href={`/search?keyword=${encodeURIComponent(keyword)}&type=dish`}
              className="flex items-center px-2 py-1.5 rounded-md hover:bg-gray-100 transition-colors"
              onClick={() => onSearch(keyword, "dish")}
            >
              <SearchIcon className="h-4 w-4 mr-3 text-gray-500" />
              <span>
                Tìm kiếm &quot;
                <span className="text-blue-600">{keyword}</span>
                &quot;
              </span>
            </Link>
          </li>
        </ul>
      </div>
    )
  }
)
KeywordSuggestions.displayName = "KeywordSuggestions"

// Search Results Dropdown Component
export const SearchResultsDropdown = memo(
  ({
    open,
    keyword,
    suggestions,
    onSearch,
    hotKeywords,
    dropdownRef,
  }: {
    open: boolean
    keyword: string
    suggestions?: KeywordSuggestion[]
    onSearch: (keyword: string, type?: string) => void
    hotKeywords: React.ReactNode
    dropdownRef: React.MutableRefObject<HTMLDivElement | null>
  }) => {
    const isKeywordAvailable = !!keyword

    return (
      <div
        ref={dropdownRef}
        className="w-full h-[calc(100%-60px)] bg-white overflow-y-auto z-50"
      >
        <div className="px-3 pt-4 pb-4">
          {isKeywordAvailable ? (
            <KeywordSuggestions
              suggestions={suggestions || []}
              keyword={keyword}
              onSearch={onSearch}
            />
          ) : (
            <>
              <RecentlySearches />
              <div className="mt-4">
                <h3 className="text-sm font-medium mb-2">Tìm kiếm phổ biến</h3>
                {hotKeywords}
              </div>
            </>
          )}
        </div>
      </div>
    )
  }
)
SearchResultsDropdown.displayName = "SearchResultsDropdown"

// Backdrop Overlay Component
export const BackdropOverlay = memo(({ onClick }: { onClick: () => void }) => (
  <div
    className="h-screen bg-[rgba(0,0,0,0.08)] z-[4] fixed top-0 left-0 right-0 overflow-auto transition-all duration-300"
    onClick={onClick}
    aria-hidden="true"
    data-testid="mobile-search-overlay"
  />
))
BackdropOverlay.displayName = "BackdropOverlay"
